<template>
  <section :class="$style.analysis">
    <div :class="[$style.analysis__plannerSelector, $style.plannerSelector]">
      <DolphinSelector
        :class="$style.plannerSelector__select"
        :options="monthOptions"
        v-model="selectedMonthValue"
      />
      <DolphinSelector
        :class="$style.plannerSelector__select"
        :options="plannerOptions"
        v-model="selectedPlannerId"
      />
      <span :class="$style.plannerSelector__text">のグラフを表示</span>
    </div>

    <MatchSummaryNumbers
      :class="[$style.analysis__numbers, $style.numbers]"
      v-bind="selectedSummary.numbers"
    />

    <div v-show="selectedSummary.statuses.length > 0">
      <MatchChart
        :class="[$style.analysis__chart, $style.chart]"
        :statuses="selectedSummary.statuses"
        :filteringPlannerId="filteringPlannerId"
        :height="320"
      />
      <MatchSummaryTable
        :class="[$style.analysis__table, $style.table]"
        :statuses="selectedSummary.statuses"
        :plannerMap="plannerMap"
        :filteringPlannerId="filteringPlannerId"
        @selectPlanner="selectPlanner"
      />
    </div>
  </section>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { defineComponent, computed, watch } from 'vue'

import { format, subMonths } from 'date-fns'

import type { SummaryNumbers } from './MatchSummaryNumbers.vue'
import MatchSummaryNumbers from './MatchSummaryNumbers.vue'
import type { PlannerMap } from './MatchSummaryTable.vue'
import MatchSummaryTable, { createPlannerMap } from './MatchSummaryTable.vue'
import MatchChart from './MatchChart.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import type { PlannerID, Option as SelectorOption } from '@/types/app'
import type {
  MatchSummaryPlannerFragment,
  MatchSummaryMatchStatusFragment,
} from './MatchSummary.generated'

type MonthKeyString = string // yyyy-MM-01
const toMonthKeyString = (date: Date): MonthKeyString =>
  format(date, 'yyyy-MM-01')
const createMonthOptionText = (month: MonthKeyString) =>
  format(new Date(month), 'yyyy年M月')

const EMPTY_FILTERING_PLANNER_ID = 'EMPTY_FILTERING_PLANNER_ID' as const

type Summary = {
  numbers: SummaryNumbers
  statuses: MatchSummaryMatchStatusFragment[]
  plannerIdMap: Record<PlannerID, PlannerID>
}

const getEmptySummary = (): Summary => ({
  numbers: { qrMatches: 0, onlineMatches: 0, messages: 0 },
  statuses: [],
  plannerIdMap: {},
})

const monthOptions = computed((): SelectorOption<MonthKeyString>[] => {
  const today = new Date()
  return Array.from({ length: 24 }).map((_, i) => {
    const monthString = toMonthKeyString(subMonths(today, i))
    return {
      text: createMonthOptionText(monthString),
      value: monthString,
    }
  })
})

export default defineComponent({
  name: 'MatchSummary',
  components: {
    DolphinSelector,
    MatchChart,
    MatchSummaryNumbers,
    MatchSummaryTable,
  },
  props: {
    statuses: {
      type: Array as PropType<ReadonlyArray<MatchSummaryMatchStatusFragment>>,
      required: true,
    },
    planners: {
      type: Array as PropType<ReadonlyArray<MatchSummaryPlannerFragment>>,
      required: true,
    },
    filteringPlannerId: {
      type: Number as PropType<Maybe<PlannerID>>,
    },
    selectedMonth: {
      type: String as PropType<Maybe<MonthKeyString>>,
    },
  },
  emits: {
    'update:filteringPlannerId': (value: Maybe<PlannerID>) =>
      value === undefined || typeof value === 'number',
    'update:selectedMonth': (value: Maybe<MonthKeyString>) =>
      value === undefined || typeof value === 'string',
  },
  setup(props, { emit }) {
    const plannerMap = computed(
      (): PlannerMap => createPlannerMap(props.planners)
    )

    const selectPlanner = (
      id: PlannerID | typeof EMPTY_FILTERING_PLANNER_ID
    ): void => {
      const value = id !== EMPTY_FILTERING_PLANNER_ID ? id : undefined
      emit('update:filteringPlannerId', value)
    }

    const selectedPlannerId = computed({
      get(): PlannerID | typeof EMPTY_FILTERING_PLANNER_ID {
        return props.filteringPlannerId ?? EMPTY_FILTERING_PLANNER_ID
      },
      set(id: PlannerID | typeof EMPTY_FILTERING_PLANNER_ID): void {
        selectPlanner(id)
      },
    })

    const selectedSummary = computed((): Summary => {
      return props.statuses.reduce<Summary>(
        ({ numbers, statuses, plannerIdMap }, status): Summary => {
          return {
            numbers: {
              qrMatches:
                numbers.qrMatches + Number(status.inflowSource === 'qr'),
              onlineMatches:
                numbers.onlineMatches +
                Number(status.inflowSource === 'normal'),
              messages: numbers.messages + status.numberOfMessages,
            },
            statuses: [...statuses, status],
            plannerIdMap: {
              ...plannerIdMap,
              [status.plannerId]: status.plannerId,
            },
          }
        },
        getEmptySummary()
      )
    })

    const selectedMonthValue = computed({
      get(): MonthKeyString {
        if (props.selectedMonth !== undefined) {
          return props.selectedMonth
        }
        return monthOptions.value[0].value
      },
      set(value: MonthKeyString): void {
        emit('update:selectedMonth', value)
      },
    })

    const plannerOptions = computed(
      (): SelectorOption<PlannerID | typeof EMPTY_FILTERING_PLANNER_ID>[] => {
        // eslint-disable-next-line fp/no-mutating-methods
        const idOptions = Object.values(selectedSummary.value.plannerIdMap)
          .sort()
          .map<SelectorOption<PlannerID>>(id => {
            return {
              text: plannerMap.value[id]?.name || '',
              value: id,
            }
          })
        return idOptions.length > 1
          ? [{ text: '全員', value: EMPTY_FILTERING_PLANNER_ID }, ...idOptions]
          : idOptions
      }
    )

    watch(
      plannerOptions,
      () => {
        if (
          props.filteringPlannerId === undefined ||
          selectedSummary.value.plannerIdMap[props.filteringPlannerId] !==
            undefined
        ) {
          return
        }
        emit('update:filteringPlannerId', undefined)
      },
      {
        immediate: true,
      }
    )

    watch(
      plannerOptions,
      (options): void => {
        if (options?.length === 1) {
          selectPlanner(options[0].value)
        }
      },
      {
        immediate: true,
      }
    )

    return {
      monthOptions,
      selectedMonthValue,
      plannerOptions,
      selectedPlannerId,
      selectedSummary,
      plannerMap,
      selectPlanner,
    }
  },
})
</script>

<style lang="scss" module>
.analysis {
  padding: 16px 16px 32px;

  &__numbers {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__chart {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__table {
    width: 100%;

    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.chart {
  height: 320px;
}

.filters {
  display: flex;
  align-items: center;
}

.plannerSelector {
  display: flex;
  align-items: center;

  &__select {
    &:nth-child(n + 2) {
      margin-left: 16px;
    }
  }

  &__text {
    color: $color-text-and-object-high-emphasis;

    &:nth-child(n + 2) {
      margin-left: 16px;
    }
  }
}
</style>
